import React, { useState, useMemo } from 'react';
import { MarketingAttributionReport } from './MarketingAttributionReport';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Download, RefreshCw,
  Users, Target, Clock, Zap, CheckCircle, Calendar, ArrowRight, Phone, Info,
  AlertTriangle,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import {
  startOfMonth, endOfMonth, subDays, subMonths, startOfYear,
  endOfYear, format, subYears,
} from 'date-fns';

// ── Types ──────────────────────────────────────────────────

interface KPIs {
  newLeads: number;
  contacted: number;
  siteVisits: number;
  designCalls: number;
  won: number;
  installed: number;
  avgLeadToWonDays: number | null;
  avgWonToInstalledDays: number | null;
}

interface MonthlyActivity {
  month: string;
  newLeads: number;
  contacted: number;
  siteVisits: number;
  designCalls: number;
  won: number;
  installed: number;
}

interface MonthlySourceBreakdown {
  month: string;
  sources: Array<{ source: string; count: number }>;
}

interface MonthlySalesCycle {
  month: string;
  avgLeadToWonDays: number | null;
  avgWonToInstalledDays: number | null;
  dealCount: number;
}

interface CohortData {
  month: string;
  leadCount: number;
  contactedCount: number;
  siteVisitCount: number;
  designCallCount: number;
  wonCount: number;
  installedCount: number;
  cohortAgeDays: number;
}

interface DashboardPeriod {
  kpis: KPIs;
  monthlyActivity: MonthlyActivity[];
  monthlySourceBreakdown: MonthlySourceBreakdown[];
  monthlySalesCycle: MonthlySalesCycle[];
}

interface DashboardResponse {
  primary: DashboardPeriod;
  compare?: DashboardPeriod;
  cohorts: CohortData[];
  stageMapping: { contactedStageIds: number[]; siteVisitStageIds: number[]; designCallStageIds: number[]; wonStageIds: number[] };
}

// ── Presets ─────────────────────────────────────────────────

type Preset = 'last15' | 'last30' | 'thisMonth' | 'lastMonth' | 'last3' | 'last6' | 'thisYear' | 'lastYear' | 'custom';

function getPresetDates(preset: Preset): { start: Date; end: Date } {
  const now = new Date();
  switch (preset) {
    case 'last15': return { start: subDays(now, 15), end: now };
    case 'last30': return { start: subDays(now, 30), end: now };
    case 'thisMonth': return { start: startOfMonth(now), end: now };
    case 'lastMonth': return { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) };
    case 'last3': return { start: subMonths(now, 3), end: now };
    case 'last6': return { start: subMonths(now, 6), end: now };
    case 'thisYear': return { start: startOfYear(now), end: now };
    case 'lastYear': return { start: startOfYear(subYears(now, 1)), end: endOfYear(subYears(now, 1)) };
    default: return { start: subMonths(now, 3), end: now };
  }
}

const PRESET_LABELS: Record<Preset, string> = {
  last15: 'Last 15 Days',
  last30: 'Last 30 Days',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  last3: 'Last 3 Months',
  last6: 'Last 6 Months',
  thisYear: 'This Year',
  lastYear: 'Last Year',
  custom: 'Custom',
};

// ── Helpers ─────────────────────────────────────────────────

function formatMonth(ym: string): string {
  const [y, m] = ym.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}

function pctChange(current: number | null, previous: number | null): { pct: number; direction: 'up' | 'down' | 'flat' } | null {
  if (current == null || previous == null || previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  return { pct: Math.round(pct), direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
}

// ── KPI Card ────────────────────────────────────────────────

function KpiCard({
  label, icon: Icon, value, compareValue, unit, invertColors,
}: {
  label: string;
  icon: React.ElementType;
  value: number | null;
  compareValue?: number | null;
  unit?: string;
  invertColors?: boolean;
}) {
  const change = compareValue !== undefined ? pctChange(value, compareValue) : null;

  const isPositive = change
    ? invertColors
      ? change.direction === 'down'
      : change.direction === 'up'
    : false;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {value != null ? value : 'N/A'}
          {unit && value != null ? <span className="text-sm font-normal text-muted-foreground ml-1">{unit}</span> : null}
        </div>
        {change && (
          <div className={`flex items-center text-xs mt-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {change.direction === 'up' ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
            {Math.abs(change.pct)}% vs compare
          </div>
        )}
        {compareValue !== undefined && compareValue != null && (
          <div className="text-xs text-muted-foreground">Compare: {compareValue}{unit ? ` ${unit}` : ''}</div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Component ──────────────────────────────────────────

const CHART_COLORS = {
  newLeads: '#3b82f6',
  contacted: '#f97316',
  siteVisits: '#10b981',
  designCalls: '#f59e0b',
  won: '#8b5cf6',
  installed: '#ec4899',
};

const SOURCE_COLORS: Record<string, string> = {
  'Meta/Facebook': '#1877F2',
  'Google': '#34A853',
  'Referral': '#F59E0B',
  'Website/Organic': '#8B5CF6',
  'Direct/Unknown': '#94A3B8',
};

export const PipelineAnalyticsDashboard: React.FC = () => {
  const [preset, setPreset] = useState<Preset>('last3');
  const [startDate, setStartDate] = useState<Date>(getPresetDates('last3').start);
  const [endDate, setEndDate] = useState<Date>(getPresetDates('last3').end);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [comparePreset, setComparePreset] = useState<Preset>('last3');
  const [compareStart, setCompareStart] = useState<Date>(getPresetDates('last3').start);
  const [compareEnd, setCompareEnd] = useState<Date>(getPresetDates('last3').end);
  const [selectedCohort, setSelectedCohort] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handlePresetChange = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      const { start, end } = getPresetDates(p);
      setStartDate(start);
      setEndDate(end);
    }
  };

  const handleComparePresetChange = (p: Preset) => {
    setComparePreset(p);
    if (p !== 'custom') {
      const { start, end } = getPresetDates(p);
      setCompareStart(start);
      setCompareEnd(end);
    }
  };

  // Build query params
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
    if (compareEnabled) {
      params.compareStartDate = compareStart.toISOString();
      params.compareEndDate = compareEnd.toISOString();
    }
    return params;
  }, [startDate, endDate, compareEnabled, compareStart, compareEnd]);

  const queryString = useMemo(() => new URLSearchParams(queryParams).toString(), [queryParams]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['management-dashboard', queryString, refreshKey],
    queryFn: async () => {
      const response = await apiRequest<{ success: boolean; data: DashboardResponse }>(
        'GET', `/api/pipeline/analytics/management-dashboard?${queryString}`
      );
      return response.data;
    },
  });

  const handleDownloadPdf = async () => {
    try {
      const res = await fetch(`/api/pipeline/analytics/management-report-pdf?${queryString}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `management-report-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
    }
  };

  // Source data for chart
  const sourceChartData = useMemo(() => {
    if (!data?.primary.monthlySourceBreakdown) return [];
    const allSources = new Set<string>();
    data.primary.monthlySourceBreakdown.forEach(mb => mb.sources.forEach(s => allSources.add(s.source)));
    return data.primary.monthlySourceBreakdown.map(mb => {
      const row: Record<string, string | number> = { month: formatMonth(mb.month) };
      for (const src of allSources) {
        row[src] = mb.sources.find(s => s.source === src)?.count || 0;
      }
      return row;
    });
  }, [data]);

  const allSourceNames = useMemo(() => {
    if (!data?.primary.monthlySourceBreakdown) return [];
    const s = new Set<string>();
    data.primary.monthlySourceBreakdown.forEach(mb => mb.sources.forEach(src => s.add(src.source)));
    return Array.from(s);
  }, [data]);

  // Source summary table
  const sourceSummary = useMemo(() => {
    if (!data?.primary.monthlySourceBreakdown) return [];
    const totals = new Map<string, number>();
    data.primary.monthlySourceBreakdown.forEach(mb =>
      mb.sources.forEach(s => totals.set(s.source, (totals.get(s.source) || 0) + s.count))
    );
    const total = Array.from(totals.values()).reduce((a, b) => a + b, 0);
    return Array.from(totals.entries())
      .sort(([, a], [, b]) => b - a)
      .map(([source, count]) => ({ source, count, pct: total > 0 ? Math.round((count / total) * 100) : 0 }));
  }, [data]);

  // Sales cycle chart data
  const salesCycleData = useMemo(() => {
    if (!data) return [];
    const rows = data.primary.monthlySalesCycle.map(m => ({
      month: formatMonth(m.month),
      'Lead→Won': m.avgLeadToWonDays,
      'Won→Installed': m.avgWonToInstalledDays,
      ...(data.compare ? (() => {
        const cm = data.compare?.monthlySalesCycle.find(c => c.month === m.month);
        return {
          'Lead→Won (compare)': cm?.avgLeadToWonDays ?? null,
          'Won→Installed (compare)': cm?.avgWonToInstalledDays ?? null,
        };
      })() : {}),
    }));
    return rows;
  }, [data]);

  // Activity chart data (with compare lines)
  const activityChartData = useMemo(() => {
    if (!data) return [];
    return data.primary.monthlyActivity.map(m => {
      const row: Record<string, string | number> = {
        month: formatMonth(m.month),
        'New Leads': m.newLeads,
        'Contacted': m.contacted,
        'Site Visits': m.siteVisits,
        'Design Calls': m.designCalls,
        Won: m.won,
        Installed: m.installed,
      };
      if (data.compare) {
        const cm = data.compare.monthlyActivity.find(c => c.month === m.month);
        row['New Leads (cmp)'] = cm?.newLeads || 0;
        row['Contacted (cmp)'] = cm?.contacted || 0;
        row['Site Visits (cmp)'] = cm?.siteVisits || 0;
        row['Design Calls (cmp)'] = cm?.designCalls || 0;
        row['Won (cmp)'] = cm?.won || 0;
        row['Installed (cmp)'] = cm?.installed || 0;
      }
      return row;
    });
  }, [data]);

  // Cumulative toggle
  const [showCumulative, setShowCumulative] = useState(false);

  const displayChartData = useMemo(() => {
    if (!showCumulative || activityChartData.length === 0) return activityChartData;
    const keys = ['New Leads', 'Contacted', 'Site Visits', 'Design Calls', 'Won', 'Installed'];
    const cmpKeys = ['New Leads (cmp)', 'Contacted (cmp)', 'Site Visits (cmp)', 'Design Calls (cmp)', 'Won (cmp)', 'Installed (cmp)'];
    const running: Record<string, number> = {};
    return activityChartData.map(row => {
      const newRow: Record<string, string | number> = { month: row.month };
      for (const key of keys) {
        running[key] = (running[key] || 0) + (Number(row[key]) || 0);
        newRow[key] = running[key];
      }
      for (const key of cmpKeys) {
        if (row[key] !== undefined) {
          running[key] = (running[key] || 0) + (Number(row[key]) || 0);
          newRow[key] = running[key];
        }
      }
      return newRow;
    });
  }, [activityChartData, showCumulative]);

  // Narrative insights — computed client-side from monthly activity data
  const narrativeInsights = useMemo(() => {
    if (!data?.primary.monthlyActivity || data.primary.monthlyActivity.length < 2) return [];
    const activity = data.primary.monthlyActivity;
    const insights: Array<{ title: string; description: string; type: 'positive' | 'warning' | 'info' }> = [];

    // Month-over-month trends (latest two months)
    const latest = activity[activity.length - 1];
    const prev = activity[activity.length - 2];
    const metrics: Array<{ key: keyof MonthlyActivity; label: string }> = [
      { key: 'newLeads', label: 'New Leads' },
      { key: 'contacted', label: 'Contacted' },
      { key: 'siteVisits', label: 'Site Visits' },
      { key: 'won', label: 'Won' },
    ];
    for (const { key, label } of metrics) {
      const curr = latest[key] as number;
      const previous = prev[key] as number;
      if (previous > 0) {
        const pctChg = Math.round(((curr - previous) / previous) * 100);
        if (Math.abs(pctChg) >= 10) {
          insights.push({
            title: `${label} ${pctChg > 0 ? 'Up' : 'Down'} ${Math.abs(pctChg)}%`,
            description: `${label} went from ${previous} in ${formatMonth(prev.month)} to ${curr} in ${formatMonth(latest.month)}.`,
            type: pctChg > 0 ? 'positive' : 'warning',
          });
        }
      }
    }

    // Stage conversion rates
    const totalLeads = activity.reduce((s, m) => s + m.newLeads, 0);
    const totalContacted = activity.reduce((s, m) => s + m.contacted, 0);
    const totalSiteVisits = activity.reduce((s, m) => s + m.siteVisits, 0);
    const totalWon = activity.reduce((s, m) => s + m.won, 0);

    if (totalLeads > 0) {
      const contactRate = Math.round((totalContacted / totalLeads) * 100);
      if (contactRate < 60) {
        insights.push({
          title: 'Low Contact Rate',
          description: `Only ${contactRate}% of leads were contacted (${totalContacted}/${totalLeads}). Consider automating initial outreach to improve follow-up speed.`,
          type: 'warning',
        });
      } else {
        insights.push({
          title: 'Strong Contact Rate',
          description: `${contactRate}% of leads were contacted (${totalContacted}/${totalLeads}) — above the 60% benchmark.`,
          type: 'positive',
        });
      }
    }

    // Funnel drop-off
    const funnelSteps = [
      { label: 'New Leads', total: totalLeads },
      { label: 'Contacted', total: totalContacted },
      { label: 'Site Visits', total: totalSiteVisits },
      { label: 'Won', total: totalWon },
    ];
    let maxDrop = 0;
    let dropFrom = '';
    let dropTo = '';
    for (let i = 1; i < funnelSteps.length; i++) {
      if (funnelSteps[i - 1].total > 0) {
        const dropPct = 1 - (funnelSteps[i].total / funnelSteps[i - 1].total);
        if (dropPct > maxDrop) {
          maxDrop = dropPct;
          dropFrom = funnelSteps[i - 1].label;
          dropTo = funnelSteps[i].label;
        }
      }
    }
    if (maxDrop > 0.3 && dropFrom) {
      insights.push({
        title: `Largest Drop-off: ${dropFrom} → ${dropTo}`,
        description: `${Math.round(maxDrop * 100)}% of ${dropFrom.toLowerCase()} did not reach ${dropTo.toLowerCase()}. This is the biggest conversion gap in the funnel.`,
        type: 'warning',
      });
    }

    // Anomaly detection — flag values > 1.5 std deviations from mean
    for (const { key, label } of metrics) {
      const values = activity.map(m => m[key] as number);
      const mean = values.reduce((s, v) => s + v, 0) / values.length;
      const stdDev = Math.sqrt(values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length);
      if (stdDev > 0) {
        const latestVal = values[values.length - 1];
        const zScore = (latestVal - mean) / stdDev;
        if (Math.abs(zScore) > 1.5) {
          insights.push({
            title: `${label} Anomaly Detected`,
            description: `${formatMonth(latest.month)} ${label.toLowerCase()} (${latestVal}) is ${zScore > 0 ? 'significantly above' : 'significantly below'} the period average (${Math.round(mean)}).`,
            type: zScore > 0 ? 'info' : 'warning',
          });
        }
      }
    }

    // Win rate insight
    if (totalLeads > 0 && totalWon > 0) {
      const winRate = Math.round((totalWon / totalLeads) * 100);
      insights.push({
        title: `Overall Win Rate: ${winRate}%`,
        description: `${totalWon} won from ${totalLeads} leads over the selected period.`,
        type: winRate >= 15 ? 'positive' : 'info',
      });
    }

    return insights;
  }, [data]);

  // Funnel data — aggregate conversion funnel across the selected period
  const funnelData = useMemo(() => {
    if (!data) return [];
    const kpis = data.primary.kpis;
    const cmpKpis = data.compare?.kpis;
    const steps = [
      { label: 'New Leads', count: kpis.newLeads, cmpCount: cmpKpis?.newLeads, color: CHART_COLORS.newLeads },
      { label: 'Contacted', count: kpis.contacted, cmpCount: cmpKpis?.contacted, color: CHART_COLORS.contacted },
      { label: 'Site Visits', count: kpis.siteVisits, cmpCount: cmpKpis?.siteVisits, color: CHART_COLORS.siteVisits },
      { label: 'Design Calls', count: kpis.designCalls, cmpCount: cmpKpis?.designCalls, color: CHART_COLORS.designCalls },
      { label: 'Won', count: kpis.won, cmpCount: cmpKpis?.won, color: CHART_COLORS.won },
      { label: 'Installed', count: kpis.installed, cmpCount: cmpKpis?.installed, color: CHART_COLORS.installed },
    ];
    const maxCount = Math.max(...steps.map(s => s.count), 1);
    return steps.map((step, i) => {
      const prevCount = i > 0 ? steps[i - 1].count : step.count;
      const dropOff = prevCount > 0 ? Math.round((1 - step.count / prevCount) * 100) : 0;
      const pctOfTotal = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
      const stepConversion = prevCount > 0 ? Math.round((step.count / prevCount) * 100) : 100;
      return { ...step, dropOff, pctOfTotal, prevCount, stepConversion };
    });
  }, [data]);

  // Find the biggest hole in the funnel (worst drop-off between consecutive stages)
  const biggestHole = useMemo(() => {
    if (funnelData.length < 2) return null;
    let worst = { from: '', to: '', dropOff: 0, idx: -1 };
    for (let i = 1; i < funnelData.length; i++) {
      if (funnelData[i].dropOff > worst.dropOff) {
        worst = { from: funnelData[i - 1].label, to: funnelData[i].label, dropOff: funnelData[i].dropOff, idx: i };
      }
    }
    return worst.dropOff > 0 ? worst : null;
  }, [funnelData]);

  // Selected cohort data
  const cohort = selectedCohort ? data?.cohorts.find(c => c.month === selectedCohort) : null;

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header + Date Controls */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Management Dashboard</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { setRefreshKey(k => k + 1); refetch(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPdf}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        {/* Date Range Controls */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Period</label>
            <Select value={preset} onValueChange={(v) => handlePresetChange(v as Preset)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PRESET_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Start</label>
            <DatePicker date={startDate} setDate={(d) => { if (d) { setStartDate(d); setPreset('custom'); } }} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">End</label>
            <DatePicker date={endDate} setDate={(d) => { if (d) { setEndDate(d); setPreset('custom'); } }} />
          </div>

          <div className="flex items-center gap-2 ml-4">
            <Switch checked={compareEnabled} onCheckedChange={setCompareEnabled} />
            <span className="text-sm text-muted-foreground">Compare</span>
          </div>

          {compareEnabled && (
            <>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Compare Period</label>
                <Select value={comparePreset} onValueChange={(v) => handleComparePresetChange(v as Preset)}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRESET_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Start</label>
                <DatePicker date={compareStart} setDate={(d) => { if (d) { setCompareStart(d); setComparePreset('custom'); } }} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">End</label>
                <DatePicker date={compareEnd} setDate={(d) => { if (d) { setCompareEnd(d); setComparePreset('custom'); } }} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          Loading dashboard data...
        </div>
      )}

      {data && (
        <>
          {/* KPI Cards */}
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-8">
            <KpiCard label="New Leads" icon={Users} value={data.primary.kpis.newLeads} compareValue={data.compare?.kpis.newLeads} />
            <KpiCard label="Contacted" icon={Phone} value={data.primary.kpis.contacted} compareValue={data.compare?.kpis.contacted} />
            <KpiCard label="Site Visits" icon={Target} value={data.primary.kpis.siteVisits} compareValue={data.compare?.kpis.siteVisits} />
            <KpiCard label="Design Calls" icon={Calendar} value={data.primary.kpis.designCalls} compareValue={data.compare?.kpis.designCalls} />
            <KpiCard label="Won" icon={CheckCircle} value={data.primary.kpis.won} compareValue={data.compare?.kpis.won} />
            <KpiCard label="Installed" icon={Zap} value={data.primary.kpis.installed} compareValue={data.compare?.kpis.installed} />
            <KpiCard label="Avg Lead→Won" icon={Clock} value={data.primary.kpis.avgLeadToWonDays} compareValue={data.compare?.kpis.avgLeadToWonDays} unit="days" invertColors />
            <KpiCard label="Avg Won→Install" icon={ArrowRight} value={data.primary.kpis.avgWonToInstalledDays} compareValue={data.compare?.kpis.avgWonToInstalledDays} unit="days" invertColors />
          </div>

          {/* Tabs */}
          <Tabs defaultValue="activity" className="space-y-4">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="attribution">Marketing attribution</TabsTrigger>
              <TabsTrigger value="activity">Monthly Activity</TabsTrigger>
              <TabsTrigger value="funnel">Pipeline Funnel</TabsTrigger>
              <TabsTrigger value="sources">Lead Sources</TabsTrigger>
              <TabsTrigger value="cycle">Sales Cycle</TabsTrigger>
              <TabsTrigger value="cohorts">Lead Cohort Tracker</TabsTrigger>
            </TabsList>

            {/* Monthly Activity Tab */}
            <TabsContent value="attribution">
              <MarketingAttributionReport from={format(startDate, 'yyyy-MM-dd')} to={format(endDate, 'yyyy-MM-dd')} />
            </TabsContent>
            <TabsContent value="activity">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Monthly Activity</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Pipeline trend lines — track volume flow through each stage over time
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={showCumulative} onCheckedChange={setShowCumulative} />
                      <span className="text-sm text-muted-foreground">Cumulative</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {displayChartData.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={displayChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="New Leads" stroke={CHART_COLORS.newLeads} strokeWidth={2} dot={{ r: 4 }} connectNulls />
                          <Line type="monotone" dataKey="Contacted" stroke={CHART_COLORS.contacted} strokeWidth={2} dot={{ r: 4 }} connectNulls />
                          <Line type="monotone" dataKey="Site Visits" stroke={CHART_COLORS.siteVisits} strokeWidth={2} dot={{ r: 4 }} connectNulls />
                          <Line type="monotone" dataKey="Design Calls" stroke={CHART_COLORS.designCalls} strokeWidth={2} dot={{ r: 4 }} connectNulls />
                          <Line type="monotone" dataKey="Won" stroke={CHART_COLORS.won} strokeWidth={2} dot={{ r: 4 }} connectNulls />
                          <Line type="monotone" dataKey="Installed" stroke={CHART_COLORS.installed} strokeWidth={2} dot={{ r: 4 }} connectNulls />
                          {data.compare && (
                            <>
                              <Line type="monotone" dataKey="New Leads (cmp)" stroke={CHART_COLORS.newLeads} strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                              <Line type="monotone" dataKey="Contacted (cmp)" stroke={CHART_COLORS.contacted} strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                              <Line type="monotone" dataKey="Site Visits (cmp)" stroke={CHART_COLORS.siteVisits} strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                              <Line type="monotone" dataKey="Design Calls (cmp)" stroke={CHART_COLORS.designCalls} strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                              <Line type="monotone" dataKey="Won (cmp)" stroke={CHART_COLORS.won} strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                              <Line type="monotone" dataKey="Installed (cmp)" stroke={CHART_COLORS.installed} strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                            </>
                          )}
                        </LineChart>
                      </ResponsiveContainer>

                      {/* Raw Data Table */}
                      <div className="rounded-md border">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="p-2 text-left font-medium">Month</th>
                              <th className="p-2 text-right font-medium" style={{ color: CHART_COLORS.newLeads }}>New Leads</th>
                              <th className="p-2 text-right font-medium" style={{ color: CHART_COLORS.contacted }}>Contacted</th>
                              <th className="p-2 text-right font-medium" style={{ color: CHART_COLORS.siteVisits }}>Site Visits</th>
                              <th className="p-2 text-right font-medium" style={{ color: CHART_COLORS.designCalls }}>Design Calls</th>
                              <th className="p-2 text-right font-medium" style={{ color: CHART_COLORS.won }}>Won</th>
                              <th className="p-2 text-right font-medium" style={{ color: CHART_COLORS.installed }}>Installed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.primary.monthlyActivity.map((m) => (
                              <tr key={m.month} className="border-b">
                                <td className="p-2 font-medium">{formatMonth(m.month)}</td>
                                <td className="p-2 text-right">{m.newLeads}</td>
                                <td className="p-2 text-right">{m.contacted}</td>
                                <td className="p-2 text-right">{m.siteVisits}</td>
                                <td className="p-2 text-right">{m.designCalls}</td>
                                <td className="p-2 text-right">{m.won}</td>
                                <td className="p-2 text-right">{m.installed}</td>
                              </tr>
                            ))}
                            <tr className="bg-muted/50 font-semibold">
                              <td className="p-2">Total</td>
                              <td className="p-2 text-right">{data.primary.monthlyActivity.reduce((s, m) => s + m.newLeads, 0)}</td>
                              <td className="p-2 text-right">{data.primary.monthlyActivity.reduce((s, m) => s + m.contacted, 0)}</td>
                              <td className="p-2 text-right">{data.primary.monthlyActivity.reduce((s, m) => s + m.siteVisits, 0)}</td>
                              <td className="p-2 text-right">{data.primary.monthlyActivity.reduce((s, m) => s + m.designCalls, 0)}</td>
                              <td className="p-2 text-right">{data.primary.monthlyActivity.reduce((s, m) => s + m.won, 0)}</td>
                              <td className="p-2 text-right">{data.primary.monthlyActivity.reduce((s, m) => s + m.installed, 0)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Narrative Insights */}
                      {narrativeInsights.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold mb-3">Insights</h4>
                          <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                            {narrativeInsights.map((insight, i) => (
                              <div
                                key={i}
                                className="rounded-lg border p-3"
                                style={{
                                  borderLeftWidth: 4,
                                  borderLeftColor: insight.type === 'positive' ? '#16a34a' : insight.type === 'warning' ? '#f59e0b' : '#3b82f6',
                                }}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  {insight.type === 'positive' && <TrendingUp className="h-4 w-4 text-green-600" />}
                                  {insight.type === 'warning' && <TrendingDown className="h-4 w-4 text-amber-500" />}
                                  {insight.type === 'info' && <Info className="h-4 w-4 text-blue-500" />}
                                  <span className="text-sm font-semibold">{insight.title}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">{insight.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      No activity data for this period
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pipeline Funnel Tab */}
            <TabsContent value="funnel">
              <Card>
                <CardHeader>
                  <CardTitle>Pipeline Funnel</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Aggregate conversion funnel — see where leads drop off between stages
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {funnelData.length > 0 ? (
                    <>
                      {/* Biggest Hole Callout */}
                      {biggestHole && biggestHole.dropOff > 0 && (
                        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-amber-900">
                              Biggest hole: {biggestHole.from} → {biggestHole.to}
                            </p>
                            <p className="text-sm text-amber-800">
                              {biggestHole.dropOff}% of {biggestHole.from.toLowerCase()} never reached {biggestHole.to.toLowerCase()}. This is the largest conversion gap in your funnel.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Visual Funnel — narrowing horizontal bars */}
                      <div className="space-y-1">
                        {funnelData.map((step, i) => (
                          <div key={step.label}>
                            {/* Bar row */}
                            <div className="flex items-center gap-3">
                              <div className="w-28 text-sm font-medium text-right flex-shrink-0">{step.label}</div>
                              <div className="flex-1 relative">
                                <div className="bg-muted rounded h-9 w-full" />
                                <div
                                  className="absolute top-0 left-0 h-9 rounded flex items-center justify-end pr-3 text-xs font-semibold text-white transition-all duration-500"
                                  style={{ width: `${Math.max(step.pctOfTotal, 3)}%`, background: step.color }}
                                >
                                  {step.count}
                                </div>
                              </div>
                              <div className="w-14 text-sm text-muted-foreground text-right flex-shrink-0">
                                {Math.round(step.pctOfTotal)}%
                              </div>
                            </div>

                            {/* Drop-off connector between bars */}
                            {i < funnelData.length - 1 && funnelData[i + 1].dropOff > 0 && (
                              <div className="flex items-center gap-3 py-0.5">
                                <div className="w-28 flex-shrink-0" />
                                <div className="flex items-center gap-2 pl-4">
                                  <span className="text-muted-foreground text-xs">↓</span>
                                  <span
                                    className="text-xs font-medium"
                                    style={{
                                      color: funnelData[i + 1].dropOff > 50 ? '#dc2626' : funnelData[i + 1].dropOff > 30 ? '#d97706' : '#16a34a',
                                    }}
                                  >
                                    {funnelData[i + 1].dropOff}% lost
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Conversion Summary Table */}
                      <div className="rounded-md border">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="p-2 text-left font-medium">Stage</th>
                              <th className="p-2 text-right font-medium">Count</th>
                              {compareEnabled && data?.compare && (
                                <th className="p-2 text-right font-medium">Compare</th>
                              )}
                              <th className="p-2 text-right font-medium">% of Leads</th>
                              <th className="p-2 text-right font-medium">Step Conversion</th>
                              <th className="p-2 text-right font-medium">Drop-off</th>
                            </tr>
                          </thead>
                          <tbody>
                            {funnelData.map((step, i) => (
                              <tr key={step.label} className="border-b">
                                <td className="p-2 font-medium flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: step.color }} />
                                  {step.label}
                                </td>
                                <td className="p-2 text-right font-semibold">{step.count}</td>
                                {compareEnabled && data?.compare && (
                                  <td className="p-2 text-right text-muted-foreground">{step.cmpCount ?? '—'}</td>
                                )}
                                <td className="p-2 text-right">{Math.round(step.pctOfTotal)}%</td>
                                <td className="p-2 text-right">{i === 0 ? '—' : `${step.stepConversion}%`}</td>
                                <td className="p-2 text-right">
                                  {i === 0 ? '—' : (
                                    <span
                                      className="font-medium"
                                      style={{
                                        color: step.dropOff > 50 ? '#dc2626' : step.dropOff > 30 ? '#d97706' : '#16a34a',
                                      }}
                                    >
                                      {step.dropOff}% lost
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      No funnel data for this period
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Lead Sources Tab */}
            <TabsContent value="sources">
              <Card>
                <CardHeader>
                  <CardTitle>Lead Sources</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {sourceChartData.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={sourceChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          {allSourceNames.map((name) => (
                            <Bar
                              key={name}
                              dataKey={name}
                              stackId="sources"
                              fill={SOURCE_COLORS[name] || '#64748b'}
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>

                      {/* Summary Table */}
                      <div className="rounded-md border">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="p-2 text-left font-medium">Source</th>
                              <th className="p-2 text-right font-medium">Count</th>
                              <th className="p-2 text-right font-medium">%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sourceSummary.map(s => (
                              <tr key={s.source} className="border-b">
                                <td className="p-2 flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full" style={{ background: SOURCE_COLORS[s.source] || '#64748b' }} />
                                  {s.source}
                                </td>
                                <td className="p-2 text-right font-medium">{s.count}</td>
                                <td className="p-2 text-right">{s.pct}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      No source data for this period
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Sales Cycle Tab */}
            <TabsContent value="cycle">
              <Card>
                <CardHeader>
                  <CardTitle>Sales Cycle Duration</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Average days from Lead→Won (blue) and Won→Installed (green), trended monthly
                  </p>
                </CardHeader>
                <CardContent>
                  {salesCycleData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={salesCycleData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="Lead→Won" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                        <Line type="monotone" dataKey="Won→Installed" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                        {data.compare && (
                          <>
                            <Line type="monotone" dataKey="Lead→Won (compare)" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                            <Line type="monotone" dataKey="Won→Installed (compare)" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                          </>
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      No sales cycle data for this period
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Lead Cohort Tracker Tab */}
            <TabsContent value="cohorts">
              <Card>
                <CardHeader>
                  <CardTitle>Lead Cohort Tracker</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Select a lead intake month to see how those specific leads progressed through stages
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {data.cohorts.length > 0 ? (
                    <>
                      <Select value={selectedCohort || ''} onValueChange={setSelectedCohort}>
                        <SelectTrigger className="w-[250px]">
                          <SelectValue placeholder="Select a cohort month" />
                        </SelectTrigger>
                        <SelectContent>
                          {data.cohorts.map(c => (
                            <SelectItem key={c.month} value={c.month}>
                              {formatMonth(c.month)} — {c.leadCount} leads
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {cohort && (
                        <div className="space-y-4">
                          {cohort.cohortAgeDays < 60 && (
                            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                              Cohort age: {cohort.cohortAgeDays} days — leads may still be progressing through stages
                            </div>
                          )}

                          {/* Funnel visualization */}
                          <div className="space-y-2">
                            {[
                              { label: 'Leads', count: cohort.leadCount, color: CHART_COLORS.newLeads },
                              { label: 'Contacted', count: cohort.contactedCount, color: CHART_COLORS.contacted },
                              { label: 'Site Visits', count: cohort.siteVisitCount, color: CHART_COLORS.siteVisits },
                              { label: 'Design Calls', count: cohort.designCallCount, color: CHART_COLORS.designCalls },
                              { label: 'Won', count: cohort.wonCount, color: CHART_COLORS.won },
                              { label: 'Installed', count: cohort.installedCount, color: CHART_COLORS.installed },
                            ].map((step, i) => {
                              const pct = cohort.leadCount > 0 ? (step.count / cohort.leadCount) * 100 : 0;
                              return (
                                <div key={step.label} className="flex items-center gap-3">
                                  <div className="w-28 text-sm font-medium text-right">{step.label}</div>
                                  <div className="flex-1 bg-muted rounded-full h-8 relative overflow-hidden">
                                    <div
                                      className="h-full rounded-full flex items-center justify-end pr-3 text-xs font-semibold text-white transition-all duration-500"
                                      style={{ width: `${Math.max(pct, 2)}%`, background: step.color }}
                                    >
                                      {step.count}
                                    </div>
                                  </div>
                                  <div className="w-16 text-sm text-muted-foreground text-right">
                                    {Math.round(pct)}%
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Summary table */}
                          <div className="rounded-md border mt-4">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b bg-muted/50">
                                  <th className="p-2 text-left">Stage</th>
                                  <th className="p-2 text-right">Count</th>
                                  <th className="p-2 text-right">% of Leads</th>
                                  <th className="p-2 text-right">Step Conversion</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  { label: 'Leads Created', count: cohort.leadCount },
                                  { label: 'Contacted', count: cohort.contactedCount },
                                  { label: 'Site Visit', count: cohort.siteVisitCount },
                                  { label: 'Design Call', count: cohort.designCallCount },
                                  { label: 'Won', count: cohort.wonCount },
                                  { label: 'Installed', count: cohort.installedCount },
                                ].map((step, i, arr) => (
                                  <tr key={step.label} className="border-b">
                                    <td className="p-2 font-medium">{step.label}</td>
                                    <td className="p-2 text-right">{step.count}</td>
                                    <td className="p-2 text-right">
                                      {cohort.leadCount > 0 ? Math.round((step.count / cohort.leadCount) * 100) : 0}%
                                    </td>
                                    <td className="p-2 text-right">
                                      {i > 0 && arr[i - 1].count > 0
                                        ? `${Math.round((step.count / arr[i - 1].count) * 100)}%`
                                        : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      No cohort data for this period
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};
